
import { supabase } from '../lib/supabase';

export interface PaymentInfo {
  amount: number;
  currency: 'SAR' | 'PKR' | 'USD';
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  bookingId: string;
}

export interface PaymentGatewayResponse {
  redirectUrl: string;
  transactionRef: string;
}

interface IPaymentGateway {
  initiate(info: PaymentInfo): Promise<PaymentGatewayResponse>;
}

class SitesWayGateway implements IPaymentGateway {
  async initiate(info: PaymentInfo): Promise<PaymentGatewayResponse> {
    return {
      redirectUrl: "SIMULATED_GATEWAY_SA",
      transactionRef: "MOCK_SA_" + Date.now()
    };
  }
}

class PaymobGateway implements IPaymentGateway {
  async initiate(info: PaymentInfo): Promise<PaymentGatewayResponse> {
    return {
      redirectUrl: "SIMULATED_GATEWAY_PK",
      transactionRef: "MOCK_PK_" + Date.now()
    };
  }
}

export class PaymentService {
  static async simulateWebhookSuccess(transactionRef: string) {
    const { data: payment } = await supabase
      .from('payments')
      .select('*')
      .eq('gateway_transaction_id', transactionRef)
      .single();

    if (payment) {
      await supabase
        .from('payments')
        .update({ payment_status: 'success' })
        .eq('id', payment.id);

      await supabase
        .from('profiles')
        .update({ isPaid: true })
        .eq('email', payment.user_email);
        
      console.log("Virtual Escrow: Payment marked as Success.");
    }
  }

  static async startPayment(info: PaymentInfo): Promise<string> {
    // Check authentication first to avoid RLS silent failures
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("Authentication required to initiate secure payment node.");
    }

    const platformFeePercent = 0.10; 
    const platformFee = Number((info.amount * platformFeePercent).toFixed(2));
    const vendorPayout = Number((info.amount - platformFee).toFixed(2));

    const gateway: IPaymentGateway = info.currency === 'SAR' ? new SitesWayGateway() : new PaymobGateway();

    try {
      const { redirectUrl, transactionRef } = await gateway.initiate(info);

      // Explicit mapping of the booking ID
      const sanitizedBookingId = (info.bookingId.includes('billing') || !info.bookingId) ? null : info.bookingId;

      const { error } = await supabase.from('payments').insert([{
        booking_id: sanitizedBookingId,
        user_email: session.user.email?.toLowerCase().trim() || info.customer.email.toLowerCase().trim(),
        gateway_provider: info.currency === 'SAR' ? 'sitesway' : 'paymob',
        gateway_transaction_id: transactionRef,
        currency: info.currency,
        total_amount: info.amount,
        platform_fee: platformFee,
        vendor_payout_amount: vendorPayout,
        payment_status: 'pending',
        payout_status: 'unpaid'
      }]);

      if (error) {
        console.error("Supabase Escrow Log Error:", error.message);
        // Specifically check for RLS policy violations
        if (error.code === '42501') {
          throw new Error("Security Policy Violation: Your account does not have permission to log this payment. Please contact admin to enable 'payments' RLS policies.");
        }
        throw new Error(`Escrow sync failed: ${error.message}`);
      }

      return redirectUrl;
    } catch (err: any) {
      console.error("Payment Initiation Error:", err.message);
      throw new Error(err.message || "Unable to connect to secure payment node.");
    }
  }
}
